import React, { useMemo, useRef, useState } from 'react';
import { Typography } from 'antd';
import { Paper } from '../common/common';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OdorZone {
  label: string;
  min: number;
  max: number;
  color: string;
  bandColor: string;
}

export interface OdorRecord {
  timestamp: number;
  intensity: number;
}

export interface OdorHistoryChartProps {
  patientId: string;
  recordListWithOdorIntensity: OdorRecord[];
  zones?: OdorZone[];
  width?: number;
  height?: number;
  showToday?: boolean;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

export const DEFAULT_ODOR_ZONES: OdorZone[] = [
  { label: 'No odor', min: 0, max: 2, color: '#42A5F5', bandColor: '#1A4A8A' },
  { label: 'Low odor', min: 2, max: 4, color: '#26C6A0', bandColor: '#1A6B55' },
  { label: 'Medium odor', min: 4, max: 6, color: '#F5A623', bandColor: '#7A4A10' },
  { label: 'High odor', min: 6, max: 10, color: '#E91E63', bandColor: '#7A1030' },
];

const CHART_BG = '#1A1836';
const HIT_RADIUS = 12; // px — invisible hover target radius

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatAxisDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function formatTooltipDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function getZone(intensity: number, zones: OdorZone[]): OdorZone {
  return zones.find((z) => intensity >= z.min && intensity <= z.max) ?? zones[zones.length - 1];
}

interface TooltipState {
  record: OdorRecord;
  cx: number; // SVG x inside the inner <g>
  cy: number; // SVG y inside the inner <g>
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const WoundKineticDisplay: React.FC<OdorHistoryChartProps> = ({ patientId, recordListWithOdorIntensity, zones = DEFAULT_ODOR_ZONES, width = 320, height = 180, showToday = false }) => {
  const PADDING = { top: 16, right: 16, bottom: 36, left: 40 };

  const chartW = width - PADDING.left - PADDING.right;
  const chartH = height - PADDING.top - PADDING.bottom;

  const minValue = zones[0].min;
  const maxValue = zones[zones.length - 1].max;

  const sorted = useMemo(() => [...recordListWithOdorIntensity].sort((a, b) => a.timestamp - b.timestamp), [recordListWithOdorIntensity]);

  const latest = sorted[sorted.length - 1];

  // ── Coordinate helpers ─────────────────────────────────────────────────────
  const minTs = sorted.length > 0 ? sorted[0].timestamp : 0;
  const maxTs = sorted.length > 0 ? sorted[sorted.length - 1].timestamp : 1;

  const toX = (ts: number) => (sorted.length < 2 ? chartW / 2 : ((ts - minTs) / (maxTs - minTs)) * chartW);

  const toY = (v: number) => chartH - ((Math.min(v, maxValue) - minValue) / (maxValue - minValue)) * chartH;

  // ── Tooltip state ──────────────────────────────────────────────────────────
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // ── Derived data ───────────────────────────────────────────────────────────
  const yTicks = useMemo(() => {
    const result: { value: number; color: string }[] = [];
    zones.forEach((z, i) => {
      if (i === 0) result.push({ value: z.min, color: z.color });
      result.push({ value: z.max, color: z.color });
    });
    return result;
  }, [zones]);

  const xLabels = useMemo(() => {
    const seen = new Set<string>();
    return sorted.map((r) => {
      const label = formatAxisDate(r.timestamp);
      const show = !seen.has(label);
      if (show) seen.add(label);
      return { ts: r.timestamp, label, show };
    });
  }, [sorted]);

  const points = sorted.map((r) => `${toX(r.timestamp)},${toY(r.intensity)}`).join(' ');

  // ── Tooltip box geometry ───────────────────────────────────────────────────
  const TOOLTIP_W = 90;
  const TOOLTIP_H = 38;
  const TOOLTIP_OFFSET = 10;

  function getTooltipX(cx: number): number {
    // Flip to left side if too close to the right edge
    if (cx + TOOLTIP_OFFSET + TOOLTIP_W > chartW) {
      return cx - TOOLTIP_OFFSET - TOOLTIP_W;
    }
    return cx + TOOLTIP_OFFSET;
  }

  function getTooltipY(cy: number): number {
    const y = cy - TOOLTIP_H / 2;
    // Clamp within chart
    return Math.max(0, Math.min(y, chartH - TOOLTIP_H));
  }

  return (
    <Paper>
      {/* ── Title ── */}
      <Typography.Title level={4} style={{ margin: '0 0 10px 0' }}>
        {patientId} odor evolution
      </Typography.Title>

      {/* ── Legend ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 12px', marginBottom: 10 }}>
        {zones.map((z) => (
          <div
            key={z.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '3px 10px 3px 7px',
              borderRadius: 20,
              background: z.bandColor,
              border: `1px solid ${z.color}66`,
            }}
          >
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: z.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: z.color, whiteSpace: 'nowrap' }}>
              {z.label}
              <span style={{ opacity: 0.6, fontWeight: 400, marginLeft: 4 }}>
                {z.min}–{z.max}
              </span>
            </span>
          </div>
        ))}
      </div>

      {/* ── Chart ── */}
      <div style={{ background: CHART_BG, borderRadius: 12, overflow: 'hidden', width: '100%' }}>
        <svg ref={svgRef} width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }} onMouseLeave={() => setTooltip(null)}>
          <defs>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <g transform={`translate(${PADDING.left}, ${PADDING.top})`}>
            {/* ── Zone bands ── */}
            {zones.map((z) => {
              const bandTop = toY(z.max);
              const bandBottom = toY(z.min);
              return <rect key={z.label} x={0} y={bandTop} width={chartW} height={bandBottom - bandTop} fill={z.bandColor} />;
            })}

            {/* ── Zone separator lines ── */}
            {zones.map((z) => (z.max < maxValue ? <line key={z.label} x1={0} y1={toY(z.max)} x2={chartW} y2={toY(z.max)} stroke="rgba(255,255,255,0.15)" strokeWidth={0.75} /> : null))}

            {/* ── Optional "today" dashed line ── */}
            {showToday && latest && <line x1={toX(latest.timestamp)} y1={0} x2={toX(latest.timestamp)} y2={chartH} stroke="rgba(255,255,255,0.2)" strokeWidth={1} strokeDasharray="4 5" />}

            {/* ── Y-axis ticks ── */}
            {yTicks.map(({ value, color }) => (
              <g key={value}>
                <line x1={-3} y1={toY(value)} x2={0} y2={toY(value)} stroke={color} strokeOpacity={0.7} strokeWidth={1} />
                <text x={-5} y={toY(value) + 3.5} textAnchor="end" fontSize={8} fill={color} fillOpacity={0.9} fontWeight={600} fontFamily="'Nunito Sans', Arial, sans-serif">
                  {value}
                </text>
              </g>
            ))}

            {/* ── Y-axis label ── */}
            <text x={-28} y={chartH / 2} textAnchor="middle" fontSize={8} fill="rgba(255,255,255,0.3)" fontFamily="'Nunito Sans', Arial, sans-serif" fontWeight={500} transform={`rotate(-90, -28, ${chartH / 2})`} letterSpacing="0.03em">
              Smell Intensity
            </text>

            {/* ── Line ── */}
            {sorted.length > 1 && <polyline points={points} fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />}

            {/* ── Data points + hit targets ── */}
            {sorted.map((r, i) => {
              const isLatest = i === sorted.length - 1;
              const isHovered = tooltip?.record === r;
              const cx = toX(r.timestamp);
              const cy = toY(r.intensity);
              const zone = getZone(r.intensity, zones);
              return (
                <g key={r.timestamp}>
                  {/* Halo on latest point */}
                  {isLatest && (
                    <>
                      <circle cx={cx} cy={cy} r={9} fill={zone.color} opacity={0.15} />
                      <circle cx={cx} cy={cy} r={6} fill={zone.color} opacity={0.25} />
                    </>
                  )}

                  {/* Visible dot */}
                  <circle
                    cx={cx}
                    cy={cy}
                    r={isLatest ? 4.5 : isHovered ? 5 : 3.5}
                    fill={zone.color}
                    stroke={isHovered ? 'white' : 'black'}
                    strokeWidth={isHovered ? 1.5 : 1.5}
                    filter={isLatest ? 'url(#glow)' : undefined}
                    style={{ transition: 'r 0.1s' }}
                  />

                  {/* Invisible hit area */}
                  <circle cx={cx} cy={cy} r={HIT_RADIUS} fill="transparent" style={{ cursor: 'crosshair' }} onMouseEnter={() => setTooltip({ record: r, cx, cy })} onMouseLeave={() => setTooltip(null)} />
                </g>
              );
            })}

            {/* ── Tooltip ── */}
            {tooltip &&
              (() => {
                const zone = getZone(tooltip.record.intensity, zones);
                const tx = getTooltipX(tooltip.cx);
                const ty = getTooltipY(tooltip.cy);
                return (
                  <g style={{ pointerEvents: 'none' }}>
                    {/* Connector line from dot to tooltip */}
                    <line x1={tooltip.cx} y1={tooltip.cy} x2={tx < tooltip.cx ? tx + TOOLTIP_W : tx} y2={ty + TOOLTIP_H / 2} stroke={zone.color} strokeWidth={0.75} strokeOpacity={0.5} strokeDasharray="2 2" />
                    {/* Box */}
                    <rect x={tx} y={ty} width={TOOLTIP_W} height={TOOLTIP_H} rx={4} fill="#12102a" stroke={zone.color} strokeWidth={1} strokeOpacity={0.7} />
                    {/* Date */}
                    <text x={tx + TOOLTIP_W / 2} y={ty + 13} textAnchor="middle" fontSize={8} fill="rgba(255,255,255,0.55)" fontFamily="'Nunito Sans', Arial, sans-serif">
                      {formatTooltipDate(tooltip.record.timestamp)}
                    </text>
                    {/* Intensity value */}
                    <text x={tx + TOOLTIP_W / 2} y={ty + 28} textAnchor="middle" fontSize={10} fontWeight={700} fill={zone.color} fontFamily="'Nunito Sans', Arial, sans-serif">
                      {tooltip.record.intensity.toFixed(1)} — {zone.label}
                    </text>
                  </g>
                );
              })()}

            {/* ── X-axis date labels ── */}
            <g transform={`translate(0, ${chartH + 5})`}>
              {xLabels
                .filter((l) => l.show)
                .map(({ ts, label }) => (
                  <text key={label} x={toX(ts)} y={16} textAnchor="middle" fontSize={9} fontWeight={500} fill="rgba(255,255,255,0.5)" fontFamily="'Nunito Sans', Arial, sans-serif">
                    {label}
                  </text>
                ))}
            </g>
          </g>
        </svg>
      </div>
    </Paper>
  );
};

export default WoundKineticDisplay;
